import type { ColumnDef } from '@tanstack/react-table';

import { Attribute } from '@/common/attribute';
import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { ModalVariant } from '@/features/configuration-management/location/table/Column';
import { SupportedLocale, useDynamicLocale } from '@/hoslog-intl/Translation';
import {
  GalleryIcon,
  IconsTags,
  KeySolidIcon,
  LockCloseIcon,
  LockOpenIcon,
  StickyNoteIcon,
  TrashIcon,
  UsersCog,
} from '@/icons';
import { GlobeSolidIcon } from '@/icons/globe-solid';
import { QRCodeSolidIcon } from '@/icons/qr-code-solid';
import { formatDate } from '@/lib/dateFormatter';
import { StoreDeletePayload } from '@/services/basicStores/basicStores.types';
import { PremiumStores, STORE_STATUS, ToggleStatusPayload } from '@/services/premiumStores/premiumStores.types';
import { Link } from '@tanstack/react-router';
import { ReactNode } from 'react';
import { Button } from 'ui/button';
import AddTagManagementForm from '../../form/AddTagManagement';
import ManageClubSubdomainFormContainer from '../../form/ManageClubSubdomainFormContainer';
import ManageStoreLineGroupForm from '../../form/ManageStoreLineGroupForm';

type ModalSize = 'sm' | 'md' | 'lg';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;

export function getColumns(
  mutate: (payload: ToggleStatusPayload) => Promise<unknown>,
  onOpen: OnOpenProps,
  resetPremiumStorePassword: (holdId: string) => Promise<{ message: string }>,
  deletePremiumStoreInfo: (payload: StoreDeletePayload) => Promise<{ message: string }>,
  t: ReturnType<typeof useDynamicLocale>['t'],
  openConfirmation: (data: {
    title: string;
    description: string;
    cancelLabel: string;
    actionLabel: string;
    content?: React.ReactNode;
    size?: ModalSize;
    variant?: ModalVariant;
    onAction: () => void | Promise<void>;
    onCancel: () => void;
  }) => void,
  locationData: Attribute[],
  locale: SupportedLocale,
  breadcrumbReturnTo: string,
  canEditLine: boolean,
  canEditSubDomain: boolean,
): ColumnDef<PremiumStores>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium text-caption text-primary truncate max-w-[10rem]">
          {getSerialNumber(table, row.index)}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'storeNameEng',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.storeInfo')} />,
      cell: ({ row }) => {
        const storeName = row.original.storeName;
        const storeNameEng = row.original.storeNameEng;
        const storeImage = row.original.clubLogo;
        const holdStatus = row.original.holdStatus;
        const status = row.original.status;

        return (
          <div className="flex items-center space-x-3 max-w-[20rem] truncate">
            <img
              src={storeImage?.path}
              alt={storeImage?.alt ?? ''}
              className="w-8 h-8 shrink-0 rounded-full object-cover"
            />
            <div className="truncate">
              {holdStatus === 'P' || status === 'B' ? (
                <Can
                  rule={{ allOf: [PERMS.club.premium.editView] }}
                  fallback={<div className="font-medium text-xs text-foreground-subtle underline">{storeNameEng}</div>}
                >
                  <div className="font-medium text-caption text-link underline cursor-not-allowed">{storeNameEng}</div>
                </Can>
              ) : (
                <Can
                  rule={{ allOf: [PERMS.club.premium.editView] }}
                  fallback={<div className="font-medium text-xs text-foreground-subtle underline">{storeNameEng}</div>}
                >
                  <Link
                    to="/store-information/premium-stores/$manage-premium-store"
                    params={{ 'manage-premium-store': row.original.clubId }}
                    search={(previous) => ({
                      createdDate: previous.createdDate,
                      breadcrumbReturnTo,
                    })}
                    className="cursor-pointer"
                  >
                    <div className="font-medium text-caption text-link underline">{storeNameEng}</div>
                  </Link>
                </Can>
              )}
              <div className="text-foreground-subtle font-medium text-caption">{storeName}</div>
            </div>
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.storeInfo',
      },
    },
    {
      accessorKey: 'telephone',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.telephone')} />,
      cell: ({ row }) => <div className="font-medium text-caption text-primary">{row.getValue('telephone')}</div>,
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.telephone',
      },
    },
    {
      accessorKey: 'clubCategory',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.category')} />,
      cell: ({ row }) => {
        return String(row.getValue('clubCategory')).toLowerCase() === 'premium' ? (
          <div className="text-link font-medium text-caption">{t('common.page.table.premium')}</div>
        ) : String(row.getValue('clubCategory')).toLowerCase() === 'gold' ? (
          <div className="text-warning font-medium text-caption">{t('common.page.table.gold')}</div>
        ) : String(row.getValue('clubCategory')).toLowerCase() === 'silver' ? (
          <div className="text-muted-foreground font-medium text-caption">{t('common.page.table.silver')}</div>
        ) : (
          <div>-</div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.category',
      },
    },
    {
      accessorKey: 'prefecture',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.location')} />,
      cell: ({ row }) => <div className="font-medium text-caption text-primary">{row.getValue('prefecture')}</div>,
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.location',
        label: t('common.page.tableColumn.location'),
        options: locationData.map((val) => {
          return {
            label: locale == 'en' ? val.labelEng : val.label,
            value: val.value,
          };
        }),
      },
    },
    {
      accessorKey: 'totalRatings',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.totalRatings')} />
      ),
      cell: ({ row }) => <div className="font-medium text-caption text-primary">{row.getValue('totalRatings')}</div>,
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.totalRatings',
      },
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) => {
        return (
          <div className="font-medium text-caption">
            {row.getValue('status') === 'A' ? (
              <div className="text-success">{t('common.page.table.active')}</div>
            ) : (
              <div className="text-destructive-text">{t('common.page.table.blocked')}</div>
            )}
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.status',
        label: t('common.page.tableColumn.status'),
        options: [
          {
            label: t('common.page.table.active'),
            value: 'A',
          },
          {
            label: t('common.page.table.blocked'),
            value: 'B',
          },
        ],
      },
      enableColumnFilter: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium text-caption text-primary whitespace-nowrap">
            {formatDate(Number(row.getValue('createdDate')), 'YYYY/MM/DD HH:mm:ss')}
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.createdDate',
      },
      enableColumnFilter: true,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.updatedDate')} />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium text-caption text-primary whitespace-nowrap">
            {formatDate(Number(row.getValue('updatedDate')), 'YYYY/MM/DD HH:mm:ss')}
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'common.page.tableColumn.updatedDate',
      },
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.action')} />,
      id: 'actions',
      cell: ({ row }) => {
        const holdStatus = row.original.holdStatus;
        const status = row.original.status;
        const name = row.original.storeNameEng;
        return (
          <div className="flex items-center gap-4">
            {holdStatus !== 'P' && status !== 'B' && (
              <>
                <Can rule={{ allOf: [PERMS.host.premium.list] }}>
                  <Link
                    to="/store-information/premium-stores/host-management/$clubid"
                    params={{ clubid: row.original.clubId }}
                    search={{ breadcrumbReturnTo }}
                  >
                    <UsersCog className="text-foreground-inactive" height={16} width={16} />
                  </Link>
                </Can>
                <Can rule={{ allOf: [PERMS.gallery.premium.list] }}>
                  <Link
                    to="/store-information/premium-stores/gallery/$store-gallery"
                    params={{ 'store-gallery': row.original.clubId }}
                    search={{ breadcrumbReturnTo }}
                  >
                    <GalleryIcon className="text-status-warning cursor-pointer" height={16} width={16} />
                  </Link>
                </Can>
                <Can rule={{ allOf: [PERMS.club.premium.plan.list] }}>
                  <Link
                    to="/store-information/premium-stores/plan-management/$clubid"
                    params={{ clubid: row.original.clubId }}
                    search={{ breadcrumbReturnTo }}
                  >
                    <StickyNoteIcon className="text-status-warning " height={16} width={16} />
                  </Link>
                </Can>
                <Can rule={{ allOf: [PERMS.club.premium.passwordReset] }}>
                  <KeySolidIcon
                    className="text-status-warning cursor-pointer"
                    height={16}
                    width={16}
                    onClick={() => {
                      openConfirmation({
                        title: t('common.page.modal.confirmation.title'),
                        description: t('storeManagement.premiumStores.resetPasswordModel.description', {
                          clubName: name,
                        }),
                        cancelLabel: t('common.page.modal.no'),
                        actionLabel: t('common.page.modal.confirm'),
                        onAction: async () => {
                          await resetPremiumStorePassword(row.original.clubId);
                        },
                        onCancel: () => {},
                        size: 'lg',
                        variant: 'warning',
                      });
                    }}
                  />
                </Can>
              </>
            )}
            {holdStatus !== 'P' && (
              <Can rule={{ allOf: [PERMS.club.premium.statusUpdate] }}>
                <Button
                  variant={'ghost'}
                  className="p-0 m-0"
                  onClick={() => {
                    openConfirmation({
                      title: t('common.page.modal.confirmation.title'),
                      description:
                        row.getValue('status') === 'A'
                          ? t('storeManagement.form.blockModel.description')
                          : t('storeManagement.form.unblockModel.description'),
                      cancelLabel: t('common.page.modal.no'),
                      actionLabel: t('common.page.modal.confirm'),
                      onAction: async () => {
                        await mutate({
                          clubId: row.original.clubId,
                          status:
                            row.getValue('status') === STORE_STATUS.APPROVED
                              ? STORE_STATUS.BLOCKED
                              : STORE_STATUS.APPROVED,
                        });
                      },
                      onCancel: () => {},
                      size: 'lg',
                      variant: 'warning',
                    });
                  }}
                >
                  {row.getValue('status') === 'A' ? (
                    <LockCloseIcon className="text-destructive-text size-4" />
                  ) : (
                    <LockOpenIcon className="text-link-secondary size-4" />
                  )}
                </Button>
              </Can>
            )}
            {holdStatus !== 'P' && status !== 'B' && (
              <>
                <IconsTags
                  className="text-foreground-subdued cursor-not-allowed"
                  height={16}
                  width={16}
                  onClick={() => {
                    const isDisabled = true;
                    if (isDisabled) {
                      return;
                    }
                    onOpen({
                      title: 'Manage HostNameGallery',
                      okText: 'Submit',
                      size: 'lg',
                      modalTitle: 'configurationManagement.commissions.manageCommissionModal.title',
                      component: <AddTagManagementForm clubId={row.original.clubId} />,
                    });
                  }}
                />
                <Can rule={{ allOf: [PERMS.club.premium.line.details] }}>
                  <QRCodeSolidIcon
                    className="text-status-success cursor-pointer "
                    height={16}
                    width={16}
                    onClick={() => {
                      onOpen({
                        modalTitle: 'storeManagement.premiumStores.modal.manageStoreLineGroup.title',
                        title: 'Manage LINE Group',
                        okText: canEditLine ? t('storeManagement.premiumStores.modal.manageStoreLineGroup.submit') : '',
                        size: 'lg',
                        component: <ManageStoreLineGroupForm clubid={row.original.clubId} />,
                      });
                    }}
                  />
                </Can>
                <Can rule={{ allOf: [PERMS.club.premium.subdomain.details] }}>
                  <GlobeSolidIcon
                    className="text-link-secondary cursor-pointer"
                    height={16}
                    width={16}
                    onClick={() => {
                      onOpen({
                        modalTitle: 'storeManagement.premiumStores.modal.manageClubSubDomain.title',
                        title: 'Manage Club Sub Domain',
                        okText: canEditSubDomain
                          ? t('storeManagement.premiumStores.modal.manageStoreLineGroup.submit')
                          : '',
                        size: 'lg',
                        component: <ManageClubSubdomainFormContainer clubId={row.original.clubId} />,
                      });
                    }}
                  />
                </Can>
                <Can rule={{ allOf: [PERMS.club.premium.delete] }}>
                  <TrashIcon
                    size={16}
                    className="cursor-pointer text-destructive-text hover:text-destructive-text"
                    onClick={() => {
                      openConfirmation({
                        title: t('common.page.modal.confirmation.title'),
                        description: t('storeManagement.form.deleteModel.description', {
                          storeName: row.original.storeNameEng,
                        }),
                        cancelLabel: t('common.page.modal.no'),
                        actionLabel: t('common.page.modal.confirm'),
                        onAction: async () => {
                          await deletePremiumStoreInfo({ clubId: row.original.clubId });
                        },
                        onCancel: () => {},
                        size: 'lg',
                        variant: 'destructive',
                      });
                    }}
                  />
                </Can>
              </>
            )}
          </div>
        );
      },
      enableHiding: false,
      size: 40,
    },
  ];
}
